/**
 * Methods for getting some useful info for videoChat module
 */

import { MAXIMUM_VIDEO_CHAT_MEMBERS, MEDIA_DEVICE_STATUSES, VIDEO_CHAT_TYPES } from "@/helpers/constants";

export default {
  /**
   * Check that VideoChat functionality is enabled
   * @param state {Object}
   * @param getters {Object}
   * @return {Boolean}
   */
  videoChatEnabled: (state, getters, rootState, rootGetters) => {
    return rootGetters["room/activeRoom"].VIDEO_CHATS_ENABLED;
  },

  /**
   * Check that VideoChat functionality is enabled and MultiParty VideoChats are enabled too
   * @param state {Object}
   * @param getters {Object}
   * @return {Boolean}
   */
  multiVideoChatEnabled: (state, getters, rootState, rootGetters) => {
    return getters.videoChatEnabled && rootGetters["room/activeRoom"].VIDEO_MULTI_PARTY;
  },

  /**
   * Video chat config
   * @param state {Object}
   * @param getters {Object}
   * @return {Object}
   */
  videoChatConfig: (state, getters, rootState) => rootState.event.config.defaultVideoChatConfig || {},

  /**
   * Maximum number of video chat participants
   * @param state {Object}
   * @param getters {Object}
   * @return {Integer}
   */
  maximumVideoChatMembers: (state, getters) => {
    if (!getters.multiVideoChatEnabled) {
      return 2;
    }
    let customMaximumVideoChatMembers = getters.videoChatConfig.maximumVideoChatMembers;
    if (customMaximumVideoChatMembers && customMaximumVideoChatMembers < MAXIMUM_VIDEO_CHAT_MEMBERS) {
      return customMaximumVideoChatMembers;
    }
    return MAXIMUM_VIDEO_CHAT_MEMBERS;
  },

  /**
   * Get number of available seats for Video Call
   * @param state {Object}
   * @param getters {Object}
   * @return {Integer}
   */
  availableNumberOfSeatsForVideoCall: (state, getters) => {
    return getters.maximumVideoChatMembers - state.videoChatMembers.length;
  },

  /**
   * Get video chat by uuid
   * @param state {Object}
   * @return {function(*): *}
   */
  videoChat: state => videoChatUUID => {
    return state.videoChats.find(chat => chat.groupUUID === videoChatUUID) || null;
  },

  /**
   * Get 1-1 video chat by recipientUUID
   * @param state {Object}
   * @return {function(*): *}
   */
  videoChatByRecipientUUID: state => recipientUUID => {
    return (
      state.videoChats.find(chat => {
        return chat.videoChatType === VIDEO_CHAT_TYPES.ONE_TO_ONE && chat.recipientUUID === recipientUUID;
      }) || null
    );
  },

  /**
   * Get active video chat data (apiKey, sessionId, token)
   * @param state {Object}
   * @return {function(): *}
   */
  activeVideoChat: state => {
    return state.activeVideoChat || null;
  },

  /**
   * Get list of VideoChat Members
   * @param state {Object}
   * @return {[]}
   */
  videoChatMembers: state => {
    return state.videoChatMembers;
  },

  /**
   * Get list of VideoChat Members UUIDs
   * @param state {Object}
   * @param getters {Object}
   * @return {[]}
   */
  videoChatMembersUUIDs: (state, getters) => {
    return getters.videoChatMembers.map(member => member.userUUID);
  },

  /**
   * Get video chat member by user uuid
   * @param state {Object}
   * @param getters {Object}
   * @return {function(*): T}
   */
  videoChatMember: (state, getters) => userUUID => {
    return getters.videoChatMembers.find(member => member.userUUID === userUUID);
  },

  /**
   * Get list of users available for a video call
   * @param state {Object}
   * @param getters {Object}
   * @return {Array}
   */
  usersAvailableForVideoCall: (state, getters, rootState, rootGetters) => {
    return rootGetters["room/roomState"].filter(user => {
      return (
        user.isOnline &&
        user.mediaDeviceStatus >= MEDIA_DEVICE_STATUSES.ALLOW &&
        getters.videoChatMembersUUIDs.indexOf(user.userUUID) === -1 &&
        user.tableUUID !== rootGetters["tables/publicTable"]?.uuid
      );
    });
  }
};
